export interface AuthUser {
  id: string;
  uid: string;
  email: string;
  emailVerified?: boolean;
  name: string;
  displayName: string;
  role: 'nutritionist' | 'patient' | string;
  crn?: string | null;
  phone?: string | null;
  gender?: string | null;
  avatarUrl?: string | null;
  clinicName?: string | null;
  clinicLogoUrl?: string | null;
  nutritionistId?: string | null;
  subscription?: Record<string, unknown>;
}

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/$/, '');
const TOKEN_KEY = 'nutrisync_access_token';
const USER_KEY = 'nutrisync_session_user';

export function getAccessToken(): string | null {
  return typeof window === 'undefined' ? null : window.sessionStorage.getItem(TOKEN_KEY);
}

export function getCurrentUser(): AuthUser | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.sessionStorage.getItem(USER_KEY);
    return value ? JSON.parse(value) as AuthUser : null;
  } catch {
    return null;
  }
}

export function saveSession(token: string, user: AuthUser): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(TOKEN_KEY, token);
  window.sessionStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearAccessToken(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(TOKEN_KEY);
  window.sessionStorage.removeItem(USER_KEY);
}

async function authRequest<T>(action: string, body?: Record<string, unknown>, authenticated = false): Promise<T> {
  const token = getAccessToken();
  const headers: Record<string, string> = { Accept: 'application/json', 'Content-Type': 'application/json' };
  if (authenticated && token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(`${API_BASE}/auth.php?action=${encodeURIComponent(action)}`, {
    method: body ? 'POST' : 'GET',
    headers,
    body: body ? JSON.stringify(body) : undefined,
    cache: 'no-store',
  });
  const result = await response.json().catch(() => null);
  if (!response.ok || !result?.success) throw new Error(result?.error || 'Não foi possível concluir a autenticação.');
  return result.data as T;
}

export async function loginWithPassword(email: string, password: string): Promise<AuthUser> {
  const result = await authRequest<{ token: string; user: AuthUser }>('login', { email, password });
  if (!result?.token || !result.user?.uid) throw new Error('Resposta de autenticação inválida.');
  saveSession(result.token, result.user);
  return result.user;
}

export async function restoreSession(): Promise<AuthUser | null> {
  if (!getAccessToken()) return null;
  try {
    const result = await authRequest<{ user: AuthUser }>('me', undefined, true);
    if (!result.user?.uid) throw new Error('Resposta de sessão inválida.');
    const token = getAccessToken();
    if (token) saveSession(token, result.user);
    return result.user;
  } catch (error) {
    clearAccessToken();
    throw error;
  }
}

export async function registerAccount(account: {
  email: string;
  password: string;
  name: string;
  role: 'nutritionist' | 'patient';
  crn?: string;
  phone?: string;
  nutritionistEmail?: string;
}): Promise<{ uid: string; verificationRequired: boolean; emailSent?: boolean; message?: string }> {
  return authRequest('register', account as unknown as Record<string, unknown>);
}

export async function createPatientAccount(email: string, password: string, name: string, userData: Record<string, unknown>): Promise<string> {
  const result = await authRequest<{ uid: string }>('create_patient_user', { email, password, name, userData }, true);
  if (!result.uid) throw new Error('O servidor não retornou o UID do paciente.');
  return result.uid;
}

export async function verifyEmail(token: string): Promise<AuthUser> {
  const result = await authRequest<{ token: string; user: AuthUser }>('verify_email', { token });
  if (!result?.token || !result.user?.uid) throw new Error('Não foi possível validar o link de confirmação.');
  saveSession(result.token, result.user);
  return result.user;
}

export async function revokeAccessToken(): Promise<void> {
  const token = getAccessToken();
  try {
    if (token) await authRequest('logout', {}, true);
  } finally {
    clearAccessToken();
  }
}

export async function requestPasswordReset(email: string): Promise<void> {
  await authRequest('forgot_password', { email });
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await authRequest('change_password', { currentPassword, newPassword }, true);
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await authRequest('reset_password', { token, newPassword });
}
